
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getUserIdFromToken, getGuestId, getSessionId, setGuestId, setSessionId, isLoggedIn, generateSessionId, getGoalLevel, getToken } from "@/utils/storage";
import { getStoredGuestExamUser } from "@/api/exam";
import { isDeliberateTrial, resolveExamAuth, setExamMode, isTaskDeliberateTrial, getExamMode } from "@/utils/examMode";
import VoiceAgentClient from '../services/voiceAgentClient';
import DeepgramTranscriptionService from '../services/deepgramService';
import ExitExamDialog from '@/components/models/ExitExamDialog';
import { useAudioWave } from '@/hooks/waveData';
import logo from './../../public/images/logo.png';
import { getStaticAudioByType, StaticAudioType } from '../config/staticAudio';
import './task1.css';
import '@/styles/speaking-task2-skip-prep.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkSubscriptionAccess } from '@/utils/subscriptionCheck';
import { isTask2MaintenanceEnabled } from '@/config/maintenance';
import Task2MaintenancePage from '@/components/task2/Task2MaintenancePage';
import {
  playTrackedLiveExamAudio,
  type PlayTrackedLiveExamAudioResult,
} from '@/lib/speaking/exam/live/liveExamRuntimeAudio';
import {
  beginLiveExamRuntimeSegment,
  isLiveExamRuntimeExiting,
  markLiveExamRuntimeExiting,
  runLiveExamRuntimeTeardown,
  scheduleLiveExamCallback,
} from '@/lib/speaking/exam/live/liveExamRuntimeTeardown';
import {
  readTask2LocalStorageTopicId,
  resolveTask2PersistenceTopicId,
} from '@/lib/speaking/exam/live/task2PersistenceTopic';
import { isSupportedTask2SkipPrepPath } from '@/lib/speaking/exam/live/task2SkipPrep';
import {
  claimTask2Ending,
  getTask2EndingClaimant,
  logTask2Ending,
  resetTask2EndingAuthority,
} from '@/lib/speaking/exam/live/task2EndingAuthority';
import {
  getTask2CompletionDestination,
  planTask2TimerExpiry,
  shouldCommitTask2CompletionNavigation,
  shouldDeferAgentTask2Navigate,
  shouldSkipTask2OnTaskCompleted,
} from '@/lib/speaking/exam/live/task2CompletionNavigation';

function getTask2ExamAuth() {
  const paidToken = getToken();
  const guestUser = getStoredGuestExamUser();
  const guestToken = guestUser?.token ?? localStorage.getItem("guestToken");
  return {
    guestUser,
    ...resolveExamAuth({
      isDeliberateTrial: isDeliberateTrial(),
      paidToken,
      guestToken,
      paidUserId: getUserIdFromToken(),
      guestUserId: guestUser?.user_id ?? null,
    }),
  };
}

const TaskTwo: React.FC = () => {
  const [status, setStatus] = useState('Ready');
  const [currentPhase, setCurrentPhase] = useState<'intro' | 'prep' | 'exam' | 'completed'>('intro');
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [aiMessages, setAiMessages] = useState<string[]>([]);
  const [aiPartial, setAiPartial] = useState<string>("");
  const [topic, setTopic] = useState('');
  const [topicId, setTopicId] = useState<string | null>(null);
  const topicIdRef = useRef<string | null>(topicId);
  topicIdRef.current = topicId;
  const prepCompleteStartedRef = useRef<boolean>(false);
  const [prepTransitionStarted, setPrepTransitionStarted] = useState(false);
  const [topicAudio, setTopicAudio] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState<string | null>(null);
  const [guardrails, setGuardrails] = useState<string | null>(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showDivider, setShowDivider] = useState(false);
  const [dividerText, setDividerText] = useState<string | null>(null);
  const [isMicActive, setIsMicActive] = useState(false);
  // Get examId from location state or localStorage (for retry attempts)
  const location = useLocation();
  const locationExamId = location.state?.examId;
  const [examId, setExamId] = useState<string | null>(
    locationExamId || localStorage.getItem("current_exam_id")
  );

  const messagesRef = useRef<HTMLDivElement | null>(null);
  const agentRef = useRef<VoiceAgentClient | null>(null);
  const deepgramRef = useRef<DeepgramTranscriptionService | null>(null);
  const phaseRef = useRef<'intro' | 'prep' | 'exam' | 'completed'>(currentPhase);
  const timerRef = useRef<number | null>(null);
  const initialInfoRef = useRef<{ topic?: string | null; topicId?: string | null; systemPrompt?: string | null; topicAudio?: string | null }>({});
  const hasStartedRef = useRef<boolean>(false);
  const hasConnectedRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const endedRef = useRef<boolean>(false);
  const toolPlayedEndingAudioRef = useRef<boolean>(false);
  const endingAudioPlayedRef = useRef<boolean>(false);
  const hasNavigatedToNextTaskRef = useRef<boolean>(false);
  const resolveAutoplayFallback = useRef<(() => void) | null>(null);
  const [showAutoplayFallback, setShowAutoplayFallback] = useState(false);
  const isRetryAttempt = localStorage.getItem('current_task_attempt_id');

  useEffect(() => {
    beginLiveExamRuntimeSegment();
    resetTask2EndingAuthority();
  }, []);

  // Check subscription access on mount
  useEffect(() => {
    const checkAccess = async () => {
      const deliberateTrial = isTaskDeliberateTrial({
        hasLocationExamId: !!locationExamId,
        hasCurrentExamId: !!localStorage.getItem("current_exam_id"),
        examMode: getExamMode(),
      });
      const result = await checkSubscriptionAccess({ deliberateTrial });
      if (!result.hasAccess) {
        console.log('❌ Task 2: Subscription access denied -', result.reason);
        window.location.href = '/subscription';
      }
    };
    checkAccess();
  }, [navigate]);

  const pushAI = (text: string) => setAiMessages(prev => [...prev, text]);

  // Helper function to try three ephemeral URLs sequentially with proper error handling
  const tryEphemeralUrls = async (
    fetchFn: (url: string) => Promise<Response>
  ): Promise<Response> => {
    const urls = [
      import.meta.env.VITE_OPENAI_EPHEMERAL_URL_VOICE,
      import.meta.env.VITE_OPENAI_EPHEMERAL_URL_EXAM,
      import.meta.env.VITE_OPENAI_EPHEMERAL_URL
    ].filter(Boolean); // Remove undefined/null values

    let lastError: Error | null = null;

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      try {
        console.log(`[EPHEMERAL-URL] Attempting URL ${i + 1}/${urls.length}: ${url ? 'configured' : 'missing'}`);
        const response = await fetchFn(url);
        
        // Check if response is ok
        if (response.ok) {
          console.log(`[EPHEMERAL-URL] ✅ Success with URL ${i + 1}/${urls.length}`);
          return response;
        } else {
          // Response received but not ok - try next URL
          console.warn(`[EPHEMERAL-URL] ⚠️ URL ${i + 1}/${urls.length} returned status ${response.status}, trying next...`);
          lastError = new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.warn(`[EPHEMERAL-URL] ❌ URL ${i + 1}/${urls.length} failed:`, error);
        lastError = error instanceof Error ? error : new Error(String(error));
        // Continue to next URL
      }
    }

    // All URLs failed
    throw lastError || new Error('All ephemeral URLs failed');
  };

  // Helper function to get the first available ephemeral URL for VoiceAgentClient connection
  // Returns the first configured URL in priority order (VOICE -> EXAM -> default)
  // VoiceAgentClient will handle connection errors and retries
  const getEphemeralUrl = (): string => {
    const url = import.meta.env.VITE_OPENAI_EPHEMERAL_URL_VOICE 
      || import.meta.env.VITE_OPENAI_EPHEMERAL_URL_EXAM 
      || import.meta.env.VITE_OPENAI_EPHEMERAL_URL;

    if (!url) {
      throw new Error('No ephemeral URLs configured');
    }

    console.log('[EPHEMERAL-URL] Using ephemeral URL:', url);
    return url;
  };

  // Handle examId from location state for retry attempts
  useEffect(() => {
    if (locationExamId) {
      setExamMode("full");
      localStorage.setItem("current_exam_id", locationExamId);
      setExamId(locationExamId);
      console.log('🎯 TASK 2 - Set examId from location state for retry:', locationExamId);
    }
  }, [locationExamId]);

  // Create Task 2 entry in tasks table
  const createTask2Entry = async (sessionId: string) => {
    const taskTopicId = resolveTask2PersistenceTopicId({
      currentRunServerTopicId: initialInfoRef.current.topicId,
      voiceAgentTopicId: agentRef.current?.getTaskTopicId?.(),
      reactTopicId: topicIdRef.current,
      localStorageTopicId: readTask2LocalStorageTopicId(),
    });
    return createTask2EntryWithTopicId(sessionId, taskTopicId);
  };

  const createTask2EntryWithTopicId = async (sessionId: string, taskTopicId: string | null) => {
    try {
      console.log('🎯 TASK 2 - Creating task entry with topicId:', taskTopicId);

      // 1) Get tokens
      const { guestUser, isGuest, token } = getTask2ExamAuth();

      if (!token) {
        console.error('❌ No paid or guest token available for task entry creation');
        return null;
      }

      // 3) Build payload
      const payload: any = {
        taskCompleted: true,
        sessionId: sessionId,
        topicId: taskTopicId
      };

      if (isGuest) {
        payload.guest_id = guestUser?.user_id;
        payload.is_guest = true;
      }

      const response = await tryEphemeralUrls((url) =>
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        })
      );

      const result = await response.json();
      console.log('✅ TASK 2 - Task entry and task_attempts entry created:', result);

      return result;
    } catch (error) {
      console.error('❌ TASK 2 - Task entry creation error:', error);
      return null;
    }
  };

  // Call grammar analysis edge function
  const callGrammarAnalysis = async (sessionId: string) => {
    try {
      // 1) Get tokens
      const { guestUser, isGuest, token } = getTask2ExamAuth();

      if (!token) {
        console.error('❌ No paid or guest token available for grammar analysis');
        return null;
      }

      // 3) Build payload
      const payload: any = {
        task_type: 2,
        session_id: sessionId
      };

      if (isGuest) {
        payload.guest_id = guestUser?.user_id;
        payload.is_guest = true;
      }

      // No delay needed - backend will handle timing

      const response = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/gramm-vocab-compre', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Grammar analysis failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Grammar analysis completed:', result);
      return result;
    } catch (error) {
      console.error('❌ Grammar analysis error:', error);
      return null;
    }
  };

  const waveData = useAudioWave({
    active: isRecording,
    bars: 24,
    gain: 4.2,
    smoothing: 0.5,
    exponent: 0.45,
  });

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSecs = seconds % 60;
    return `${minutes}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  const handleAutoplayFallback = async () => {
    console.log('[VOICE-PAGE] User clicked autoplay fallback');
    const ctx = (window as any).__appAudioContext;
    if (ctx) {
      try {
        await ctx.resume();
        console.log('[VOICE-PAGE] AudioContext resumed by user gesture');
      } catch (e) {
        console.error('[VOICE-PAGE] Failed to resume context in fallback:', e);
      }
    }
    setShowAutoplayFallback(false);
    if (resolveAutoplayFallback.current) {
      resolveAutoplayFallback.current();
      resolveAutoplayFallback.current = null;
    }
  };

  const navigateTask2CompletionOnce = useCallback((reason: string) => {
    if (!shouldCommitTask2CompletionNavigation({
      runtimeExiting: isLiveExamRuntimeExiting(),
      alreadyNavigated: hasNavigatedToNextTaskRef.current,
    })) {
      console.log('[VOICE-PAGE] Task 2 completion navigation skipped', {
        reason,
        runtimeExiting: isLiveExamRuntimeExiting(),
        alreadyNavigated: hasNavigatedToNextTaskRef.current,
      });
      return false;
    }
    hasNavigatedToNextTaskRef.current = true;
    const destination = getTask2CompletionDestination(!!localStorage.getItem('current_task_attempt_id'));
    console.log('[VOICE-PAGE] Task 2 completion navigating', { reason, destination });
    navigate(destination);
    return true;
  }, [navigate]);

  const navigateTask2AfterEndingAudio = useCallback(
    (reason: string, postEndDelayMs = 0) => {
      if (postEndDelayMs > 0) {
        scheduleLiveExamCallback(() => navigateTask2CompletionOnce(reason), postEndDelayMs);
      } else {
        navigateTask2CompletionOnce(reason);
      }
    },
    [navigateTask2CompletionOnce],
  );

  const playStaticAudioUrl = async (
    url: string,
    onEnd?: () => void,
  ): Promise<PlayTrackedLiveExamAudioResult | 'skipped'> => {
    console.log('[VOICE-PAGE] playStaticAudioUrl called with URL:', url);
    if (isLiveExamRuntimeExiting()) {
      console.log('[VOICE-PAGE] playStaticAudioUrl skipped - runtime exiting');
      return 'skipped';
    }

    try {
      const ctx: AudioContext | undefined = (window as Window & { __appAudioContext?: AudioContext }).__appAudioContext;
      if (ctx) {
        try {
          await ctx.resume();
          console.log('[VOICE-PAGE] WebAudio: AudioContext resumed, state:', ctx.state);
        } catch (e) {
          console.warn('[VOICE-PAGE] WebAudio: ctx.resume() failed:', e);
        }

        if (ctx.state === 'suspended' && !isLiveExamRuntimeExiting()) {
          console.warn('[VOICE-PAGE] AudioContext still suspended after resume attempt - showing fallback');
          setShowAutoplayFallback(true);
          await new Promise<void>(res => {
            resolveAutoplayFallback.current = res;
          });
          try { await ctx.resume(); } catch {}
        }
      }

      if (isLiveExamRuntimeExiting()) return 'skipped';

      try {
        console.log('[VOICE-PAGE] WebAudio: disabling mic before playback');
        agentRef.current?.setMicEnabled(false);
      } catch (e) {
        console.warn('[VOICE-PAGE] WebAudio: setMicEnabled(false) failed:', e);
      }

      let result = await playTrackedLiveExamAudio({
        url,
        audioContext: (window as Window & { __appAudioContext?: AudioContext }).__appAudioContext,
        onEnd,
      });

      if (result === 'aborted') {
        if (isLiveExamRuntimeExiting()) {
          console.log('[VOICE-PAGE] playStaticAudioUrl aborted - runtime exiting');
        } else {
          console.log('[VOICE-PAGE] playStaticAudioUrl aborted - playback interrupted');
        }
        return result;
      }

      if (result === 'failed' && !isLiveExamRuntimeExiting()) {
        console.log('[VOICE-PAGE] Showing autoplay fallback due to HTMLAudio failure');
        setShowAutoplayFallback(true);
        await new Promise<void>(res => {
          resolveAutoplayFallback.current = res;
        });
        if (isLiveExamRuntimeExiting()) return 'skipped';
        result = await playTrackedLiveExamAudio({
          url,
          audioContext: (window as Window & { __appAudioContext?: AudioContext }).__appAudioContext,
          onEnd,
        });
      }
      return result;
    } catch (err) {
      console.error('[VOICE-PAGE] playStaticAudioUrl exception', err);
      return isLiveExamRuntimeExiting() ? 'aborted' : 'failed';
    }
  };

  const playTask2StaticEndingThenNavigate = useCallback(
    async (reasonPrefix: string, url: string, postEndDelayMs = 0) => {
      if (isLiveExamRuntimeExiting()) return;
      endingAudioPlayedRef.current = true;
      const result = await playStaticAudioUrl(url, () => {
        navigateTask2AfterEndingAudio(`${reasonPrefix}-ending-audio`, postEndDelayMs);
      });
      if (result === 'failed' && !isLiveExamRuntimeExiting()) {
        navigateTask2CompletionOnce(`${reasonPrefix}-ending-playback-failed`);
      }
    },
    [navigateTask2AfterEndingAudio, navigateTask2CompletionOnce],
  );

  const stopAllInput = () => {
    endedRef.current = true;
    setIsRecording(false);
    // Disable microphone when stopping all input
    if (agentRef.current) {
      agentRef.current.setMicEnabled(false);
      console.log('[VOICE-PAGE] Microphone DISABLED - stopping all input');
    }
    if (deepgramRef.current) {
      deepgramRef.current.cleanup();
    }
  };

  const startExamSequence = () => {
    console.log('[VOICE-PAGE] ✅ startExamSequence CALLED - Starting exam phase');
    const startExam = getStaticAudioByType('start_exam', 'task2');
    if (!startExam) { 
      console.error('[VOICE-PAGE] ❌ start_exam audio not found - cannot proceed');
      return; 
    }
    
    console.log('[VOICE-PAGE] Playing start_exam audio...');
    pushAI(startExam.text);
    
    // Timeout fallback: ensure exam starts even if audio fails
    let examStarted = false;
    const examStartTimeout = setTimeout(() => {
      if (examStarted) return;
      console.warn('[VOICE-PAGE] ⏱️ start_exam audio timeout - forcing exam start');
      examStarted = true;
      initializeExam();
    }, 5000); // 5 seconds timeout
    
    playStaticAudioUrl(startExam.url, async () => {
      if (examStarted) {
        console.log('[VOICE-PAGE] ⏭ Exam already started via timeout');
        return;
      }
      clearTimeout(examStartTimeout);
      examStarted = true;
      console.log('[VOICE-PAGE] ✅ start_exam audio finished - starting exam phase');
      initializeExam();
    });

    // Initialize exam function
    function initializeExam() {
      
      console.log('[VOICE-PAGE] ✅ Initializing exam phase');
      setCurrentPhase('exam');
      phaseRef.current = 'exam';
      setRemainingSeconds(210);
      agentRef.current?.setRemainingSeconds(210);
      setIsRecording(true);
      setExamStarted(true);
      setTimerActive(true);
      console.log('[VOICE-PAGE] State updated: exam phase, 5 seconds, recording active');

      // ENABLE microphone for exam phase
      if (agentRef.current) {
        agentRef.current.setMicEnabled(true);
        console.log('[VOICE-PAGE] ✅ Microphone ENABLED for exam phase');
      } else {
        console.warn('[VOICE-PAGE] ⚠️ agentRef.current is null - cannot enable microphone');
      }
      setStatus('Exam started - mic enabled');
      setIsMicActive(true);

      // Start Deepgram and wire to agent speech handler
      console.log('[VOICE-PAGE] Initializing Deepgram transcription service...');
      deepgramRef.current = new DeepgramTranscriptionService({
        onInterimResult: (t) => {
          if (endedRef.current || phaseRef.current !== 'exam') return;
          agentRef.current?.handleUserSpeech(t, '');
        },
        onFinalResult: (t) => {
          if (endedRef.current || phaseRef.current !== 'exam') return;

          const trimmedText = t.trim();
          if (!trimmedText) {
            console.log('[VOICE-PAGE] Skipping empty final result');
            return;
          }

          // NEW: Store transcript immediately when we get final result
          if (agentRef.current && trimmedText) {
            const sessionId = agentRef.current.getSessionId();
            console.log('[VOICE-PAGE] Immediately storing final transcript:', {
              text: trimmedText.substring(0, 50) + '...',
              sessionId: sessionId,
              timestamp: new Date().toISOString()
            });

            // Store locally first
            // No longer needed - all storage is direct to DB now

            // Then store to database immediately
            if (sessionId) {
              const payload = {
                sessionId: sessionId,
                transcript: trimmedText,
                role: 'user',
                user_id: getUserIdFromToken(),  // Add user_id for logged-in users
                guest_id: getGuestId()          // Add guest_id for guests
              }

              // Get token for authentication
              const { token } = getTask2ExamAuth();

              // Send to backend immediately with authentication
              const headers: any = {
                'Content-Type': 'application/json'
              };

              // Add Authorization header if token is available
              if (token) {
                headers['Authorization'] = `Bearer ${token}`;
                console.log('[VOICE-PAGE] 🔐 Sending transcript with auth token');
              } else {
                console.warn('[VOICE-PAGE] ⚠️ No token available for transcript storage');
              }

              tryEphemeralUrls((url) =>
                fetch(url, {
                  method: 'POST',
                  headers: headers,
                  body: JSON.stringify(payload)
                })
              )
                .then(response => {
                  if (response.ok) {
                    console.log('[VOICE-PAGE] ✅ Transcript stored to backend immediately');
                  } else {
                    console.warn('[VOICE-PAGE] ⚠️ Failed to store transcript to backend immediately:', response.status);
                  }
                })
                .catch(error => {
                  console.error('[VOICE-PAGE] ❌ Error storing transcript to backend immediately (all URLs failed):', error);
                });
            }
          }

          agentRef.current?.handleUserSpeech('', trimmedText);
        }
      });
      deepgramRef.current.start();
      console.log('[VOICE-PAGE] ✅ Deepgram started successfully');

      // Update session instructions with system prompt if available
      if (systemPrompt) {
        console.log('[VOICE-PAGE] System prompt available, length:', systemPrompt.length);
        // Note: System prompt is already set in the session configuration
      }

      // Start exam timer
      console.log('[VOICE-PAGE] Starting exam timer interval...');
      if (timerRef.current) {
        console.log('[VOICE-PAGE] Clearing existing timer before starting exam timer');
        window.clearInterval(timerRef.current);
      }
      timerRef.current = window.setInterval(async () => {
        setRemainingSeconds((s) => {
          console.log('[VOICE-PAGE] 🕐 Exam timer tick - remaining:', s);
          if (s <= 1) {
            console.log('[VOICE-PAGE] ⏰ Exam timer expired - ending task');
            if (timerRef.current) window.clearInterval(timerRef.current);
            stopAllInput();
            setCurrentPhase('completed');
            console.log('[VOICE-PAGE] Timer expired - stopping all audio and playing ending audio');

            // CRITICAL: Stop all AI audio responses immediately when timer expires
            if (agentRef.current) {
              console.log('[VOICE-PAGE] Stopping all AI audio immediately...');
              agentRef.current.stopAllAudioCompletely();
            }

            const timerPlan = planTask2TimerExpiry({
              runtimeExiting: isLiveExamRuntimeExiting(),
              canClaimTimerEnding: claimTask2Ending('timer'),
            });
            if (timerPlan === 'silent_abort') {
              return 0;
            }
            if (timerPlan === 'play_ending_and_navigate') {
              const endAudio = getStaticAudioByType('end', 'task2');
              if (!endAudio) {
                console.warn('[VOICE-PAGE] end audio not found');
                navigateTask2CompletionOnce('timer-missing-end-audio');
                return 0;
              }
              console.log('[VOICE-PAGE] Playing ending audio immediately...');

              try { agentRef.current?.stop(); hasConnectedRef.current = false; } catch { }

              const sessionId = getSessionId();
              if (sessionId) {
                createTask2Entry(sessionId).catch(error => {
                  console.error('Task 2 entry creation failed:', error);
                });

                const isRetryAttempt = localStorage.getItem('current_task_attempt_id');
                if (isRetryAttempt) {
                  console.log('🎯 TASK 2 RETRY - Calling grammar analysis for retry attempt');
                  callGrammarAnalysis(sessionId).catch(error => {
                    console.error('Grammar analysis failed:', error);
                  });
                } else {
                  console.log('🎯 TASK 2 NORMAL FLOW - Skipping grammar analysis (will be handled by final scoring)');
                }
              }

              void playTask2StaticEndingThenNavigate('timer', endAudio.url);
            } else if (timerPlan === 'defer_authoritative_ending') {
              console.log(
                '[VOICE-PAGE] Timer expired while authoritative Task 2 ending in progress — skipping duplicate ending',
              );
            }
            return 0;
          }
          const next = s - 1;
          agentRef.current?.setRemainingSeconds(next);
          return next;
        });
      }, 1000);
    }
  };

  const completePrepAndStartExam = () => {
    if (prepCompleteStartedRef.current) {
      console.log('[VOICE-PAGE] Prep complete transition already started - ignoring duplicate');
      return;
    }
    prepCompleteStartedRef.current = true;
    setPrepTransitionStarted(true);

    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    startExamSequence();
  };

  const handleSkipPrep = () => {
    completePrepAndStartExam();
  };

  const startPrepCountdown = () => {
    console.log('[VOICE-PAGE] ✅ startPrepCountdown CALLED - Starting prep phase');
    setCurrentPhase('prep');
    phaseRef.current = 'prep';
    const TASK2_PREP_COUNTDOWN = import.meta.env.VITE_TASK2_PREP_COUNTDOWN || 120;
    setRemainingSeconds(TASK2_PREP_COUNTDOWN);
    setStatus('Prep time started - mic disabled');

    // Ensure microphone is disabled during prep phase (defensive - mic should already be disabled)
    if (agentRef.current) {
      agentRef.current.setMicEnabled(false);
      console.log('[VOICE-PAGE] Microphone DISABLED for prep phase');
    }

    console.log('[VOICE-PAGE] Starting prep countdown interval...');
    timerRef.current = window.setInterval(() => {
      setRemainingSeconds((s) => {
        console.log('[VOICE-PAGE] 🕐 Prep countdown tick - remaining:', s);
        if (s <= 1) {
          console.log('[VOICE-PAGE] ✅ Prep countdown complete - starting exam via canonical transition');
          completePrepAndStartExam();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    console.log('[VOICE-PAGE] Prep countdown interval started successfully');
  };

  
const start = useCallback(async () => {
  if (hasStartedRef.current) { 
    console.log('[VOICE-PAGE] start() skipped - already started'); 
    return; 
  }
  hasStartedRef.current = true;
  endingAudioPlayedRef.current = false;
  hasNavigatedToNextTaskRef.current = false;

  console.log('[VOICE-PAGE] start() called. Testing ephemeral URLs...');
  if (agentRef.current) { 
    console.log('[VOICE-PAGE] Existing agentRef found, skipping new connect'); 
    return; 
  }
  
  // ===== ENHANCED MOBILE AUDIO CONTEXT HANDLING =====
  try {
    const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (AC) {
      let ctx: AudioContext | undefined = (window as any).__appAudioContext;
      if (!ctx || ctx.state === 'closed') {
        console.log('🔧 Creating new AudioContext...');
        ctx = new AC();
        (window as any).__appAudioContext = ctx;
      }

      console.log('🔧 AudioContext state:', ctx.state);

      // Force resume audio context for mobile
      if (ctx.state === 'suspended') {
        console.log('🔧 Resuming suspended AudioContext...');
        await ctx.resume();
        // Additional mobile-specific resume
        if (ctx.state === 'suspended') {
          console.log('🔧 AudioContext still suspended, retrying...');
          await new Promise(resolve => setTimeout(resolve, 100));
          await ctx.resume();
        }
      }

      // Keep audio context alive on mobile
      const keepAlive = () => {
        if (ctx && ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
      };

      // Resume audio context on user interaction (mobile requirement)
      document.addEventListener('touchstart', keepAlive, { once: true });
      document.addEventListener('touchend', keepAlive, { once: true });
      document.addEventListener('click', keepAlive, { once: true });
    }
    console.log('🔓 Audio context unlocked for Task 2 (mobile optimized)');
  } catch (e) {
    console.warn('AudioContext unlock failed:', e);
  }
  // ===== END ENHANCED MOBILE AUDIO CONTEXT HANDLING =====

  // Get session ID - use examId for retry attempts, otherwise get from localStorage
  const sessionId = examId || getSessionId();
  console.log('[VOICE-PAGE] Using session ID for conversation tracking:', sessionId);
  console.log('[VOICE-PAGE] Session ID source:', examId ? 'examId (retry)' : 'localStorage (normal)');

  // Get static audio info early
  const intro = getStaticAudioByType('intro', 'task2');
  const startPrep = getStaticAudioByType('start_prep', 'task2');
  const startExam = getStaticAudioByType('start_exam', 'task2');
  if (!intro || !startPrep || !startExam) { 
    console.warn('[VOICE-PAGE] Missing static audio assets'); 
    return; 
  }

  // Start intro audio immediately while connecting in parallel
  pushAI(intro.text);
  console.log('[VOICE-PAGE] Starting intro audio immediately...');

    // Start parallel connection and audio playback
    const connectionPromise = (async () => {
      // Get ephemeral URL (prioritizes VITE_OPENAI_EPHEMERAL_URL_VOICE, then EXAM, then default)
      // VoiceAgentClient will handle connection errors
      const epUrl = getEphemeralUrl();
      console.log('[VOICE-PAGE] Using ephemeral URL for connection:', epUrl);

      // Get user ID for both logged-in and guest users
      const { userId } = getTask2ExamAuth();

      console.log('[VOICE-PAGE] Creating VoiceAgentClient with sessionId:', sessionId);
      console.log('[VOICE-PAGE] User ID for transcript storage:', userId);
      agentRef.current = new VoiceAgentClient(
        {
          ephemeralUrl: epUrl,
          model: import.meta.env.VITE_OPENAI_REALTIME_MODEL,
          voice: import.meta.env.VITE_OPENAI_REALTIME_VOICE,
          sessionId: sessionId,// Pass the session ID
          userId: userId, // Pass the user ID for transcript storage
          topicId: topicId, // Pass the topic ID for task completion
        },
        {
          onStatusChange: (s) => {
            console.log('[VOICE-PAGE] status:', s);
            setStatus(s);
            if (s === 'Playing task ending') {
              toolPlayedEndingAudioRef.current = true;
            }
          },
          // onAIResponse: (resp) => { if (resp?.text) { console.log('[VOICE-PAGE] onAIResponse (final):', resp.text); setAiPartial(""); pushAI(resp.text); } },

          onAIResponse: (resp) => {
            if (resp?.text) {
              setAiPartial("");

              pushAI(resp.text);
            }
          },

          onAIStreamDelta: (t) => { setAiPartial(t || ""); },
          onTaskCompleted: async (info) => {
            if (isLiveExamRuntimeExiting()) {
              console.log('[VOICE-PAGE] onTaskCompleted skipped - runtime exiting');
              return;
            }

            if (
              shouldSkipTask2OnTaskCompleted({
                claimant: getTask2EndingClaimant(),
                agentReportedPlayedAudio: !!info?.playedAudio,
                endingAlreadyStartedRef: endingAudioPlayedRef.current,
              })
            ) {
              console.log('[VOICE-PAGE] Ending already handled/in progress — skipping onTaskCompleted', {
                claimant: getTask2EndingClaimant(),
                playedAudio: !!info?.playedAudio,
              });
              return;
            }

            stopAllInput();
            phaseRef.current = 'completed';
            setCurrentPhase('completed');
            setRemainingSeconds(0);
            if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
            toolPlayedEndingAudioRef.current = !!info?.playedAudio;

            if (agentRef.current) {
              console.log('[VOICE-PAGE] Stopping all AI audio from onTaskCompleted...');
              agentRef.current.stopAllAudioCompletely();
            }

            const sessionId = getSessionId();
            if (sessionId) {
              const taskTopicId = info?.topicId || topicId;
              createTask2EntryWithTopicId(sessionId, taskTopicId).catch(error => {
                console.error('Task 2 entry creation failed:', error);
              });

              const isRetryAttempt = localStorage.getItem('current_task_attempt_id');
              if (isRetryAttempt) {
                console.log('🎯 TASK 2 RETRY - Calling grammar analysis for retry attempt');
                callGrammarAnalysis(sessionId).catch(error => {
                  console.error('Grammar analysis failed:', error);
                });
              } else {
                console.log('🎯 TASK 2 NORMAL FLOW - Skipping grammar analysis (will be handled by final scoring)');
              }
            }

            if (!info?.playedAudio) {
              const endAudio = getStaticAudioByType('end', 'task2');
              if (!endAudio) {
                console.warn('[VOICE-PAGE] end audio not found');
                navigateTask2CompletionOnce('onTaskCompleted-missing-end-audio');
                return;
              }
              if (!claimTask2Ending('onTaskCompleted')) {
                console.log('[VOICE-PAGE] onTaskCompleted static ending skipped — ending already claimed');
                return;
              }
              console.log('[VOICE-PAGE] Playing ending audio from onTaskCompleted...');
              void playTask2StaticEndingThenNavigate('onTaskCompleted', endAudio.url);
            } else {
              console.log('[VOICE-PAGE] Ending audio already played by agent, navigating');
              navigateTask2CompletionOnce('onTaskCompleted-agent-played');
            }
          },
          onInitialData: (d) => {
            console.log('[VOICE-PAGE] onInitialData:', {
              hasTopic: !!d?.topic,
              hasTopicId: !!d?.topicId,
              hasSystemPrompt: !!d?.systemPrompt,
              hasGuardrails: !!d?.guardrails,
              hasTopicAudio: !!d?.topicAudio,
              topicPreview: d?.topic ? d.topic.substring(0, 60) + '...' : 'none',
              topicId: d?.topicId,
              systemPromptLength: d?.systemPrompt ? d.systemPrompt.length : 0,
              guardrailsLength: d?.guardrails ? d.guardrails.length : 0,
              topicAudioLength: d?.topicAudio ? d.topicAudio.length : 0
            });

            // Check if this is a retry attempt and get topic preference
            const isRetryAttempt = localStorage.getItem('current_task_attempt_id');
            const useSameTopic = localStorage.getItem('task2_retry_use_same_topic') === 'true'; // This is now set in the request

            if (isRetryAttempt) {
              console.log('🎯 TASK 2 RETRY - Retry detected, topic preference:', useSameTopic);
            }

            // Always use the topic data provided by the backend
            if (d?.topic) { setTopic(d.topic); initialInfoRef.current.topic = d.topic; }
            if (d?.topicId) {
              setTopicId(d.topicId);
              initialInfoRef.current.topicId = d.topicId;
              // Also set the topicId in the VoiceAgentClient for task completion
              if (agentRef.current) {
                agentRef.current.setTopicId(d.topicId);
              }
            }

            if (d?.systemPrompt) { setSystemPrompt(d.systemPrompt); initialInfoRef.current.systemPrompt = d.systemPrompt; }
            if (d?.guardrails) { setGuardrails(d.guardrails); }
            if (d?.topicAudio) { setTopicAudio(d.topicAudio); initialInfoRef.current.topicAudio = d.topicAudio; }
          },
          onError: (e) => { console.error('[VOICE-PAGE] onError:', e); setStatus(e); },
          onNavigate: (path) => {
            console.log('[VOICE-PAGE] Tool requested navigation to:', path);
            if (path === '/task-three' || path === '/task2-processing') {
              if (shouldDeferAgentTask2Navigate(getTask2EndingClaimant())) {
                console.log('[VOICE-PAGE] agent-onNavigate deferred — page-owned Task 2 ending in progress', {
                  claimant: getTask2EndingClaimant(),
                });
                return;
              }
              navigateTask2CompletionOnce('agent-onNavigate');
              return;
            }
            if (isLiveExamRuntimeExiting()) return;
            navigate(path);
          }
        }
      );

      // Get preferred topic ID for retries from "Explore Templates"
      const preferredTopicId = localStorage.getItem('preferred_topic_id');
      if (preferredTopicId) {
        localStorage.removeItem('preferred_topic_id'); // Clean up immediately
      }

      const ok = await agentRef.current.connect({ preferredTopicId });
      if (!ok) { setStatus('❌ Failed to connect Realtime'); return false; }
      hasConnectedRef.current = true;
      
      // Expose agentRef globally so Navbar can detect if test is running
      (window as any).agentRef = agentRef;

      // Ensure mic is OFF initially and during intro/prep phases
      agentRef.current.setMicEnabled(false);
      return true;
    })();

    // Play intro audio immediately (doesn't wait for connection)
    await playStaticAudioUrl(intro.url, async () => {
      console.log('[Task 2] Intro finished. Waiting for connection and checking topic audio...');

      // Wait for connection to complete if it hasn't already
      const connectionOk = await connectionPromise;
      if (!connectionOk) {
        setStatus('❌ Task 2 Failed to connect after intro');
        return;
      }else{
        console.log('[Task 2] Connected after intro');
      } 

      const { isGuest, token: guestOrPaidToken } = getTask2ExamAuth();
      console.log('[Task 2] Is guest:', isGuest, 'Has trial/guest-or-paid token:', !!guestOrPaidToken);
      if (isGuest) {
        // For guest: use static trail-topic for Task 2
        
        const { getStaticAudioByType } = await import('../config/staticAudio');
        const trail = getStaticAudioByType('trail-topic', 'task2');
        console.log('[Task 2] Guest user, using static trail-topic:', trail);
        if (trail) {
          setTopic(trail.text);
          // Play static topic audio (URL)
          await playStaticAudioUrl(trail.url);
        } else {
          console.warn('[Task 2] No static trail-topic found for Task 2');
        }
      } else {
        // Non-guest: Play backend topic TTS if available
        const topicB64 = initialInfoRef.current.topicAudio || topicAudio;
        if (topicB64) {
          // Try WebAudio first
          const ctx: AudioContext | undefined = (window as any).__appAudioContext;
          const playWithWebAudio = async (b64: string) => {
            if (!ctx) throw new Error('No AudioContext');
            if (ctx.state === 'closed') {
              console.warn('[Task 2] AudioContext is closed, skipping WebAudio playback');
              throw new Error('AudioContext is closed');
            }
            try {
              await ctx.resume();
              console.log('[Task 2] AudioContext resumed successfully');
            } catch (e) {
              console.warn('[Task 2] Failed to resume AudioContext:', e);
              throw new Error('Failed to resume AudioContext');
            }
            // Decode base64 to ArrayBuffer
            const bin = atob(b64);
            const len = bin.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
            const buffer = await new Promise<AudioBuffer>((resolve, reject) => ctx.decodeAudioData(bytes.buffer, resolve as any, reject));
            return await new Promise<void>((resolve) => {
              try {
                if (ctx.state === 'closed') {
                  console.warn('[VOICE-PAGE] AudioContext closed during playback, skipping');
                  resolve();
                  return;
                }
                const src = ctx.createBufferSource();
                src.buffer = buffer;
                src.connect(ctx.destination);
                src.onended = () => { console.log('[VOICE-PAGE] Topic TTS (WebAudio): ended'); resolve(); };
                agentRef.current?.setMicEnabled(false);
                console.log('[VOICE-PAGE] Topic TTS (WebAudio): start - mic remains disabled');
                src.start(0);
              } catch (e) {
                console.error('[VOICE-PAGE] Topic TTS (WebAudio) start failed', e);
                resolve();
              }
            });
          };
          let played = false;
          if (ctx) {
            try {
              await playWithWebAudio(topicB64);
              played = true;
              console.log('[VOICE-PAGE] Topic TTS finished - mic remains disabled until exam phase');
            } catch (e) {
              console.warn('[VOICE-PAGE] Topic TTS WebAudio failed, will fallback', e);
            }
          }
          if (!played) {
            try {
              const dataUrl = `data:audio/mp3;base64,${topicB64}`;
              console.log('[VOICE-PAGE] Playing topic TTS (HTMLAudio fallback)...');
              const el = new Audio(dataUrl);
              try { (el as any).playsInline = true; el.setAttribute('playsinline', 'true'); } catch { }
              el.onloadstart = () => console.log('[VOICE-PAGE] Topic TTS: loadstart');
              el.oncanplay = () => console.log('[VOICE-PAGE] Topic TTS: canplay');
              el.onplay = () => console.log('[VOICE-PAGE] Topic TTS: play fired');
              el.onended = () => console.log('[VOICE-PAGE] Topic TTS: ended');
              el.onerror = () => console.error('[VOICE-PAGE] Topic TTS: error', el.error);
              await new Promise<void>((resolve) => {
                el.onended = () => { console.log('[VOICE-PAGE] Topic TTS finished - mic remains disabled until exam phase'); resolve(); };
                el.onerror = () => { console.error('[VOICE-PAGE] Topic TTS failed', el.error); resolve(); };
                agentRef.current?.setMicEnabled(false);
                el.play().catch((err) => { console.error('[VOICE-PAGE] Topic TTS play() rejected', err); resolve(); });
              });
            } catch (e) {
              console.error('[VOICE-PAGE] Topic TTS playback exception (fallback)', e);
            }
          }
        }
      }

      pushAI(startPrep.text);
      console.log('[VOICE-PAGE] ✅ Pushed start_prep text to AI messages');
      console.log('[VOICE-PAGE] 🔊 Playing start_prep audio...', startPrep.url);
      
      // Timeout fallback: ensure prep countdown starts even if audio fails
      let prepCountdownStarted = false;
      const prepStartTimeout = setTimeout(() => {
        if (prepCountdownStarted) return;
        console.warn('[VOICE-PAGE] ⏱️ start_prep audio timeout - forcing prep countdown start');
        prepCountdownStarted = true;
        startPrepCountdown();
      }, 5000); // 5 seconds timeout
      
      await playStaticAudioUrl(startPrep.url, () => {
        if (prepCountdownStarted) {
          console.log('[VOICE-PAGE] ⏭ Prep countdown already started via timeout');
          return;
        }
        clearTimeout(prepStartTimeout);
        prepCountdownStarted = true;
        console.log('[VOICE-PAGE] ✅ start_prep audio finished - starting prep countdown');
        startPrepCountdown();
      });
    });
  }, [topicAudio, systemPrompt, guardrails]);

  useEffect(() => {
    console.log('[VOICE-PAGE] useEffect mount → auto start once');
    // Prevent direct URL access: require a session/exam id (retry or stored)
    // Allow retry attempts (when current_task_attempt_id exists in localStorage)
    const isRetryAttempt = !!localStorage.getItem('current_task_attempt_id');
    const sessionId = examId || getSessionId();
    if (!sessionId && !isRetryAttempt) {
      console.warn('[VOICE-PAGE] No session/exam id found and not a retry - redirecting to dashboard to prevent direct access');
      // ✅ FIX: Use React Router navigate instead of window.location.href to avoid iOS auth issues
      navigate('/dashboard', { replace: true });
      return;
    }
    start();
    return () => {
      console.log('[VOICE-PAGE] Unmount cleanup');
      if (hasConnectedRef.current && agentRef.current) agentRef.current.stop();
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (deepgramRef.current) deepgramRef.current.cleanup();
    };
  }, [start]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [aiMessages]);

  // Also autoscroll on streaming partial updates
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [aiPartial]);

  // useEffect(() => {
  //   if (currentPhase === 'completed') {
  //     setTimeout(() => {
  //       setDividerText('Task 2 complete');
  //       setShowDivider(true);
  //       setTimeout(() => setShowDivider(false), 900);
  //     }, 350);
  //   }
  // }, [currentPhase]);

  useEffect(() => {
  if (!examStarted || !isRecording || !timerActive) return;

  const healthCheckInterval = setInterval(() => {
    if (deepgramRef.current && typeof deepgramRef.current.isActive === 'function') {
      const isActive = deepgramRef.current.isActive();
      const isHealthy = deepgramRef.current.isHealthy?.() ?? true;

      if (!isActive || !isHealthy) {
        console.log("🔄 Voice detection not active/healthy, restarting...");
        deepgramRef.current.start();
      } else {
        // Only log occasionally to reduce noise
        if (remainingSeconds % 15 === 0) {
          console.log("✅ Voice detection is active and healthy");
        }
      }
    }
  }, 5000); // Check every 5 seconds

  return () => clearInterval(healthCheckInterval);
}, [examStarted, isRecording, timerActive, remainingSeconds]);

// Mobile-specific touch handling
useEffect(() => {
  if (!examStarted || !isRecording) return;

  const handleTouchStart = () => {
    // Resume audio context on touch (mobile requirement)
    const ctx = (window as any).__appAudioContext;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    // Ensure microphone is active on touch
    if (deepgramRef.current && typeof deepgramRef.current.isActive === 'function') {
      const isActive = deepgramRef.current.isActive();
      if (!isActive) {
        console.log("🔄 Touch detected - restarting voice detection");
        deepgramRef.current.start();
      }
    }
  };

    // Add touch event listeners for mobile
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchStart, { passive: true });
  
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchStart);
    };
  }, [examStarted, isRecording]);
  
  // Special handling for last 30 seconds on mobile
  useEffect(() => {
    if (!examStarted || !isRecording || remainingSeconds >= 30) return;
  
    console.log("📱 Last 30 seconds - ensuring mobile microphone stays active");
  
    // Force restart voice detection in last 30 seconds
    const lastPhaseInterval = setInterval(() => {
      if (deepgramRef.current && typeof deepgramRef.current.isActive === 'function') {
        const isActive = deepgramRef.current.isActive();
        if (!isActive) {
          console.log("🔄 Last 30 seconds - restarting voice detection on mobile");
          deepgramRef.current.start();
        }
      }
    }, 2000); // Check every 2 seconds in last 30 seconds
  
    return () => clearInterval(lastPhaseInterval);
  }, [examStarted, isRecording, remainingSeconds]);

  // Handle browser back button - redirect to mic-test and stop all audio
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      markLiveExamRuntimeExiting();
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Stop all audio and recording immediately
      if (agentRef.current) {
        agentRef.current.stopAllAudio();
        agentRef.current.stop();
      }

      if (deepgramRef.current) {
        deepgramRef.current.cleanup();
      }

      // Stop any local audio elements
      try {
        const audioElements = document.querySelectorAll('audio');
        audioElements.forEach(audio => {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        });

        // Stop Web Audio API contexts
        const audioContext = (window as any).__appAudioContext;
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close().catch(() => { });
        }
      } catch (error) {
        console.warn('Error during audio cleanup on back button:', error);
      }

      // Redirect to mic-test page
      navigate('/mic-test', { replace: true });
    };

    // Add event listener
    window.addEventListener('popstate', handlePopState);

    // Push a state to prevent immediate back navigation
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  const handleExitExam = () => setShowExitModal(true);

  const handleConfirmExit = () => {
    markLiveExamRuntimeExiting();
    setShowExitModal(false);
    setIsRecording(false);
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop all audio and voice agent
    stopAllInput();

    // Enhanced audio cleanup - comprehensive like Task 1
    if (agentRef.current) {
      // Stop all AI audio responses immediately
      agentRef.current.stopAllAudio();
      agentRef.current.stop();
      // Explicitly close global audio context on exit
      agentRef.current.cleanupGlobalAudio();
    }

    // Stop any local audio elements
    try {
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
      });

      // Stop Web Audio API contexts - handled by cleanupGlobalAudio above, but keeping as fallback
      // const audioContext = (window as any).__appAudioContext;
      // if (audioContext && audioContext.state !== 'closed') {
      //   audioContext.close().catch(() => { });
      // }
    } catch (error) {
      console.warn('Error during audio cleanup:', error);
    }

    // Reset all state
    setCurrentPhase('intro');
    phaseRef.current = 'intro';
    setRemainingSeconds(0);
    setAiMessages([]);
    setTopic('');
    setStatus('Ready to start');
    hasConnectedRef.current = false;
    hasStartedRef.current = false;
    
    // ✅ FIX: Use React Router navigate instead of window.location.href
    // This prevents hard reloads that can cause authentication issues on iOS
    // The small delay ensures cleanup completes before navigation
    setTimeout(() => {
      const isAuthenticated = !!localStorage.getItem("access_token");
      if (isAuthenticated) {
        window.location.href = '/dashboard';
      } else {
        window.location.href = 'https://prep2pass.ca';
      }
    }, 100);
  };

  const handleContinueExam = () => setShowExitModal(false);

  const handleFinishTaskClick = async () => {
    logTask2Ending('cta_click', {
      claimant: getTask2EndingClaimant(),
      toolPlayedEndingAudioRef: toolPlayedEndingAudioRef.current,
      endedRef: endedRef.current,
    });

    if (!claimTask2Ending('cta')) {
      logTask2Ending('cta_ignored_already_claimed');
      return;
    }

    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AC) {
        let ctx = (window as any).__appAudioContext;
        if (!ctx) {
          ctx = new AC();
          (window as any).__appAudioContext = ctx;
        }
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    } catch (e) {
      console.warn('Failed to unlock audio context:', e);
    }

    console.log('[VOICE-PAGE] Stopping all input & marking phase as completed...');
    stopAllInput();
    phaseRef.current = 'completed';
    setCurrentPhase('completed');
    setRemainingSeconds(0);

    if (agentRef.current) {
      agentRef.current.stopAllAudioCompletely();
      agentRef.current.stop();
    }

    try {
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        if (!audio.paused) audio.pause();
      });
    } catch (error) {
      console.warn('[VOICE-PAGE] Error stopping HTML audio elements:', error);
    }

    await new Promise(resolve => setTimeout(resolve, 200));
    if (isLiveExamRuntimeExiting()) return;

    const sessionId = getSessionId();
    if (sessionId) {
      createTask2Entry(sessionId).catch(error => {
        console.error('[VOICE-PAGE] ❌ Task 2 entry creation failed:', error);
      });

      const isRetryAttempt = localStorage.getItem('current_task_attempt_id');
      if (isRetryAttempt) {
        callGrammarAnalysis(sessionId).catch(error => {
          console.error('[VOICE-PAGE] ❌ Grammar analysis failed:', error);
        });
      }
    }

    const endAudio = getStaticAudioByType('end', 'task2');
    if (!endAudio) {
      navigateTask2CompletionOnce('cta-missing-end-audio');
      return;
    }
    if (!endAudio.url) {
      navigateTask2CompletionOnce('cta-missing-end-audio-url');
      return;
    }

    logTask2Ending('cta_play_static', {
      source: 'playStaticAudioUrl',
      url: endAudio.url,
      type: endAudio.type,
    });

    try {
      const audioContext = (window as any).__appAudioContext;
      if (audioContext && audioContext.state === 'suspended') {
        await audioContext.resume();
      }
    } catch (error) {
      console.warn('[VOICE-PAGE] Error resuming audio context:', error);
    }

    setDividerText('Task 2 complete');
    setShowDivider(true);
    void playTask2StaticEndingThenNavigate('cta', endAudio.url, 800);
  };




  if (isTask2MaintenanceEnabled()) {
    return <Task2MaintenancePage />;
  }

  return (
    <main id="exam" className="exam-main" aria-label="TCF Speaking Exam Task 2 (RTC Voice Agent)">
      <header className="exam-header">
        <div className="header-inner">
          <div className="header-left">
            <span className="task-badge" aria-current="step">{isRetryAttempt ? "Tâche 2" : "Tâche 2 sur 3"}</span>
          </div>

          <div className="header-right" role="group" aria-label="Status">
            {currentPhase === 'prep' ? (
              <time
                className={`timer ${remainingSeconds === 0 ? 'freeze' : remainingSeconds > 0 ? 'active' : 'idle'}`}
                aria-live="polite"
                aria-atomic="true"
                dateTime={`PT${remainingSeconds}S`}
              >
                {formatTime(remainingSeconds)}
              </time>
              // ) : currentPhase === 'completed' ? (
              //   <time className={`timer freeze`} aria-live="polite" aria-atomic="true">Task Completed</time>
            ) : (
              <time
                className={`timer ${remainingSeconds === 0 ? 'freeze' : remainingSeconds > 0 ? 'active' : 'idle'}`}
                aria-live="polite"
                aria-atomic="true"
                dateTime={`PT${remainingSeconds}S`}
              >
                {formatTime(remainingSeconds)}
              </time>
            )}

            <span className="recording" role="status" aria-live="polite">
              <span className="record-dot" aria-hidden="true" />
              <span className="record-text">
                {currentPhase === "completed"
                  ? "Microphone Inactive"
                  : isMicActive
                    ? "Microphone Active"
                    : "Microphone Inactive"}
              </span>

              <span className={`record-bars ${isRecording && currentPhase !== 'completed' ? 'animate' : ''}`} aria-hidden="true">

                <i /><i /><i /><i /><i />
              </span>
            </span>
          </div>

          <div>
            <button type="button" className="exit-btn" onClick={handleExitExam} aria-label="Exit Exam">Quitter l’examen</button>
          </div>
        </div>
      </header>

      <section className="content">
        <div className="ai-messages" aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }} ref={messagesRef}>
          {aiMessages.length > 0 || (aiPartial && aiPartial.trim()) ? (
            <>
              {aiMessages.map((msg, idx) => (
                <div key={`ai-msg-${idx}`} className="ai-bubble">
                  <div className="ai-qus-wrapper">
                    <div className="ai-left">
                      <img src={logo} alt="Logo" className="ai-icon" />
                      <strong className="ai-label">AI:</strong>
                    </div>
                    <p className={`ai-text ${idx < aiMessages.length - 1 ? 'text-gray dim' : 'fade-in'}`}>
                      {msg}
                    </p>
                  </div>
                </div>
              ))}

              {aiPartial && aiPartial.trim() && (
                <div key="ai-partial" className="ai-bubble">
                  <div className="ai-qus-wrapper">
                    <div className="ai-left">
                      <img src={logo} alt="Logo" className="ai-icon" />
                      <strong className="ai-label">AI:</strong>
                    </div>
                    <p className="ai-text fade-in" style={{ opacity: 1 }}>
                      {aiPartial}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="ai-bubble">
              <div className="ai-qus-wrapper ai-qus-wrapper--shell" aria-hidden="true" />
            </div>
          )}
        </div>

        <div
          className={`prompt-card topic-reveal ${isRecording ? 'is-recording' : 'is-idle'}`}
          role="region"
          aria-label="Task 2 Prompt"
        >
          <div>
            {topic ? (
              <p className="prompt-title topic-reveal">
              {topic}
            </p>
            ) : (
              <>
                {currentPhase === 'intro' && (<p className="prompt-title">Préparation de la tâche 2 …</p>)}
              </>
            )}
          </div>
          <div className={`card-wave ${isRecording ? 'active' : 'idle'}`} aria-hidden="true">
            {waveData.map((amp, i) => (
              <span key={i} style={{ transform: `scaleY(${isRecording ? amp : 1})` }} />
            ))}
          </div>
        </div>
        <div className="finish-button-container">
          {isSupportedTask2SkipPrepPath(location.pathname) &&
            currentPhase === 'prep' &&
            !prepTransitionStarted && (
            <div className="w-full flex justify-end gap-2">
              <button
                type="button"
                className="skip-prep-btn"
                onClick={handleSkipPrep}
                disabled={prepTransitionStarted}
              >
                Skip preparation
              </button>
            </div>
          )}
          {currentPhase === 'exam' && (
            <div className="w-full flex justify-end gap-2">
              <button className="finish-btn" onClick={handleFinishTaskClick}>Terminer la tâche</button>
            </div>
          )}
        </div>

      </section>

      {/* {showDivider && (
          <div className="divider-overlay" aria-live="polite">
            <span className="divider-chip">{dividerText}</span>
          </div>

        )} */}

      {/* Autoplay Fallback Overlay */}
      {showAutoplayFallback && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm">
          <div className="text-center p-8 max-w-md mx-4">
            <div className="mb-6 text-6xl">🔊</div>
            <h3 className="text-2xl font-bold text-white mb-4">Tap to Start Audio</h3>
            <p className="text-gray-300 mb-8">
              iOS requires a tap to play the audio for this task.
            </p>
            <button
              onClick={handleAutoplayFallback}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold text-lg shadow-lg transition-all transform hover:scale-105 active:scale-95 w-full"
            >
              Start Task 2
            </button>
          </div>
        </div>
      )}

      <ExitExamDialog
        open={showExitModal}
        onOpenChange={(open) => {
          setShowExitModal(open);
        }}
        onContinue={handleContinueExam}
        onConfirm={handleConfirmExit}
      />
    </main>
  );
};

export default TaskTwo;
